// Новое заявление о реквизитах. Панель, а не модалка: заявление можно завести заново, ничего
// необратимого здесь нет — прежнее просто перестанет быть действующим.
//
// Номер карты вводится целиком и уходит на сервер, где шифруется; обратно приезжают только
// последние четыре цифры. То есть после сохранения сверить введённое глазами уже нельзя —
// поэтому поле подписано так, чтобы человек проверил номер ДО отправки.
//
// Состав полей меняется вместе со способом перевода и бланком: у заказчика их два, обычный и
// официальный (со ссылкой на статью 136 ТК и полными банковскими реквизитами). Показывать оба
// набора сразу значит предлагать заполнить лишнее, а лишнее заполненное поле — это лишний
// повод ошибиться.
//
// До 26.08.2026 эта фраза стояла в комментарии, а полей официального бланка в форме не было
// вовсе: ни БИК, ни корреспондентского счёта, ни расчётного, ни места под скан самого
// заявления. В договоре с сервером они были и лежали неиспользованными — то есть заявление по
// официальному бланку завести было нельзя, а комментарий утверждал обратное.

import type { FormEvent } from 'react';
import { useRef, useState } from 'react';

import { RiErrorWarningLine } from '@remixicon/react';

import { uploadFile } from '@/shared/api/endpoints/accountable';
import type { ApplicationIn, PayoutMethod } from '@/shared/api/endpoints/payouts';
import { useAuthStore } from '@/shared/auth/store';
import { can } from '@/shared/rbac/permissions';
import * as Alert from '@/shared/ui/alert';
import { Skeleton } from '@/shared/ui/screen-state';
import * as Button from '@/shared/ui/button';
import { DateField } from '@/shared/ui/date-field';
import { ErrorAlert } from '@/shared/ui/error-alert';
import { FilePicker } from '@/shared/ui/file-picker';
import * as Hint from '@/shared/ui/hint';
import { SelectField } from '@/shared/ui/select-field';
import * as Switch from '@/shared/ui/switch';
import { TextField } from '@/shared/ui/text-field';

import { useCreatePayoutApplicationMutation, useEmployeeQuery } from '../queries';

const EDIT_PERMISSION = 'core.payout.edit';

const СПОСОБЫ: readonly { value: PayoutMethod; label: string }[] = [
  { value: 'card', label: 'На карту' },
  { value: 'sbp', label: 'По номеру телефона' },
];

export function PayoutPanel({ id, onClose }: { id: string; onClose: () => void }) {
  const permissions = useAuthStore((state) => state.permissions);
  const allowed = can(permissions, EDIT_PERMISSION);
  const employee = useEmployeeQuery(id);
  const mutation = useCreatePayoutApplicationMutation(id);

  const [method, setMethod] = useState<string>('card');
  const [bank, setBank] = useState('');
  const [card, setCard] = useState('');
  const [phone, setPhone] = useState('');
  const [recipient, setRecipient] = useState('');
  const [applicationDate, setApplicationDate] = useState('');
  const [официальный, setОфициальный] = useState(false);
  const [бик, setБик] = useState('');
  const [корсчёт, setКорсчёт] = useState('');
  const [расчётный, setРасчётный] = useState('');
  const [скан, setСкан] = useState<File | null>(null);
  const [грузим, setГрузим] = useState(false);
  const [ошибкаРеквизита, setОшибкаРеквизита] = useState<string | undefined>(undefined);
  const [запрет, setЗапрет] = useState<string | null>(null);
  // Засов от второй отправки. Именно засов, а не состояние: состояние обновляется к следующей
  // отрисовке, а между двумя нажатиями подряд её не происходит — оба нажатия читают «не идёт»,
  // оба грузят скан и оба заводят заявление. Пока кнопка гасла на время сохранения, это было
  // не видно; кнопка гаснуть перестала (выключенная не доводит нажатие до обработчика и потому
  // ничего не объясняет), и защиту пришлось сделать настоящей.
  const идёт = useRef(false);

  const наКарту = method === 'card';

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!allowed) {
      setЗапрет(
        'Заводить реквизиты может администратор или финансовый отдел. Право выдаёт администратор.',
      );
      return;
    }
    if (идёт.current || грузим || mutation.isPending) {
      setЗапрет('Заявление уже сохраняется, ждём ответ сервера. Второй запрос не отправляем.');
      return;
    }

    const номер = card.replace(/\s/g, '');
    if (наКарту && номер.length < 12) {
      setОшибкаРеквизита('Введите номер карты целиком: по нему делается перевод.');
      return;
    }
    if (!наКарту && phone.trim() === '') {
      setОшибкаРеквизита('Введите номер телефона: по нему делается перевод.');
      return;
    }
    if (официальный && бик.trim() === '') {
      setЗапрет(
        'В официальном бланке БИК обязателен: без него банковские реквизиты неполные, и бухгалтерия заказчика такое заявление не примет.',
      );
      return;
    }
    setОшибкаРеквизита(undefined);
    setЗапрет(null);
    идёт.current = true;

    // Скан уходит ПЕРЕД заявлением и отдельным запросом: при обрыве теряется загрузка файла,
    // а не заполненная форма. Не загрузился — заявление не сохраняем вовсе: заявление без
    // подписанного бланка нечем подтвердить, если человек скажет «я такого не подавал».
    let сканId: string | null = null;
    if (скан !== null) {
      setГрузим(true);
      try {
        сканId = (await uploadFile(скан, 'document')).id;
      } catch {
        setГрузим(false);
        идёт.current = false;
        setЗапрет(
          'Скан заявления не загрузился. Заявление не сохранили: попробуйте выбрать файл ещё раз или сохраните без скана.',
        );
        return;
      }
      setГрузим(false);
    }

    const body: ApplicationIn = {
      employee_id: id,
      method: method as PayoutMethod,
      bank_name: bank.trim() === '' ? null : bank.trim(),
      card_number: наКарту ? номер : null,
      phone: наКарту ? null : phone.trim(),
      recipient_name: recipient.trim() === '' ? null : recipient.trim(),
      application_date: applicationDate === '' ? null : applicationDate,
      // Полные банковские реквизиты живут только в официальном бланке. Снятый переключатель
      // отправляет пустоту, а не прежде введённое: иначе выключенный бланк молча уносил бы с
      // собой счета, которых в нём нет.
      bik: официальный && бик.trim() !== '' ? бик.trim() : null,
      corr_account: официальный && корсчёт.trim() !== '' ? корсчёт.trim() : null,
      settlement_account: официальный && расчётный.trim() !== '' ? расчётный.trim() : null,
      doc_file_id: сканId,
    };
    // Засов снимается и на успехе, и на отказе: после отказа сервера человек правит поле и
    // отправляет снова, и запертая форма выглядела бы как зависшая программа.
    mutation.mutate(body, {
      onSuccess: onClose,
      onSettled: () => {
        идёт.current = false;
      },
    });
  }

  return (
    <form
      onSubmit={(событие) => void handleSubmit(событие)}
      noValidate
      className='flex flex-col gap-3'
    >
      {/* Пока имя едет — скелет, а не слово «загружаем…»: серая строка текста на месте
          будущего имени читается как ЗНАЧЕНИЕ («чьё это заявление? загружаем карточку»), а
          человек в этот момент уже начал заполнять форму. Скелет говорит «сюда приедет», а
          не подставляет ответ. */}
      {/* Скелет показывается ТОЛЬКО пока запрос идёт. Условие «данных нет» держало бы его
          вечно: карточка может не приехать вовсе — например, панель открыли по ссылке на
          человека, которого больше не видно по правам. Вечный скелет читается как «сейчас
          догрузится» и не догружается никогда. */}
      {employee.data === undefined && employee.isPending ? (
        <Skeleton className='h-4 w-40' />
      ) : employee.data === undefined ? null : (
        <p className='text-paragraph-xs text-text-sub-600'>{employee.data.display_name}</p>
      )}

      {!allowed && (
        <Alert.Root variant='light' status='warning' size='small' role='status'>
          <Alert.Icon as={RiErrorWarningLine} />
          <span>
            Права на реквизиты у вас нет: форму можно заполнить, но заявление не сохранится. Право
            выдаёт администратор.
          </span>
        </Alert.Root>
      )}

      <Alert.Root variant='light' status='information' size='small' role='status'>
        <Alert.Icon as={RiErrorWarningLine} />
        <span>
          Новое заявление заменит действующее. Прежнее останется в истории — по нему видно, куда
          переводили раньше.
        </span>
      </Alert.Root>

      <SelectField
        id='payout-method'
        label='Способ перевода'
        required
        value={method}
        onChange={setMethod}
        options={СПОСОБЫ.map((с) => ({ value: с.value, label: с.label }))}
        placeholder='Выберите способ'
      />

      <TextField id='payout-bank' label='Банк' maxLength={255} value={bank} onChange={setBank} />

      {наКарту ? (
        <>
          <TextField
            id='payout-card'
            label='Номер карты'
            required
            maxLength={25}
            value={card}
            onChange={setCard}
            placeholder='2200 7010 1234 5678'
            error={ошибкаРеквизита}
          />
          <Hint.Root>
            Проверьте номер сейчас: после сохранения система показывает только последние четыре
            цифры, сверить его целиком будет уже нельзя.
          </Hint.Root>
        </>
      ) : (
        <TextField
          id='payout-phone'
          label='Номер телефона'
          required
          maxLength={32}
          value={phone}
          onChange={setPhone}
          placeholder='+7 900 123-45-67'
          error={ошибкаРеквизита}
        />
      )}

      <TextField
        id='payout-recipient'
        label='Получатель, если это другой человек'
        maxLength={255}
        value={recipient}
        onChange={setRecipient}
        placeholder='Фамилия Имя Отчество'
      />
      <Hint.Root>
        Заполняется, только когда перевод идёт третьему лицу: в бланке заказчика такое поле есть.
      </Hint.Root>

      <DateField
        id='payout-date'
        label='Дата заявления'
        value={applicationDate}
        onChange={setApplicationDate}
      />

      {/* Бланков у заказчика два, и различает их не оформление, а состав: официальный ссылается
          на статью 136 ТК и требует полных банковских реквизитов. Переключатель, а не выбор из
          двух строк: вариантов ровно два, и один из них — обычный, то есть значение по
          умолчанию. */}
      <label className='flex items-center gap-2 text-paragraph-sm text-text-sub-600'>
        <Switch.Root checked={официальный} onCheckedChange={setОфициальный} />
        Официальный бланк со ссылкой на статью 136 ТК
      </label>

      {официальный && (
        <>
          <TextField
            id='payout-bik'
            label='БИК банка'
            required
            maxLength={9}
            inputMode='numeric'
            value={бик}
            onChange={setБик}
            placeholder='046577964'
          />
          <TextField
            id='payout-corr'
            label='Корреспондентский счёт'
            maxLength={20}
            inputMode='numeric'
            value={корсчёт}
            onChange={setКорсчёт}
            placeholder='30101810500000000964'
          />
          <TextField
            id='payout-settlement'
            label='Расчётный счёт получателя'
            maxLength={20}
            inputMode='numeric'
            value={расчётный}
            onChange={setРасчётный}
            placeholder='40817810100000000123'
            hint='Счёт человека, а не карты: в официальном бланке заказчик указывает именно его.'
          />
        </>
      )}

      <FilePicker
        id='payout-scan'
        label='Скан заявления'
        подсказка='Выбрать снимок или скан подписанного заявления'
        file={скан}
        onPick={setСкан}
        disabled={грузим}
      />
      <Hint.Root>
        Прикладывается подписанный бланк. Без него заявление сохранится, но подтвердить, что человек
        его подавал, будет нечем.
      </Hint.Root>

      {запрет === null ? null : (
        <Alert.Root variant='light' status='warning' size='small' role='status'>
          <Alert.Icon as={RiErrorWarningLine} />
          <span>{запрет}</span>
        </Alert.Root>
      )}

      {mutation.error ? <ErrorAlert error={mutation.error} /> : null}

      <div className='flex items-center gap-2'>
        {/* Кнопка не гаснет на время сохранения: выключенная не доводит нажатие до
            обработчика, и повторное нажатие выглядело бы как сломанная программа. Второй
            запрос отсекается в самом обработчике, и там же написано, почему. */}
        <Button.Root type='submit' variant='primary' size='small'>
          {грузим ? 'Грузим скан…' : mutation.isPending ? 'Сохраняем…' : 'Сохранить заявление'}
        </Button.Root>
        <Button.Root type='button' variant='neutral' mode='stroke' size='small' onClick={onClose}>
          Отмена
        </Button.Root>
      </div>
    </form>
  );
}
